<template>
    <div class="gender-select-wrapper" :class="{ 'is-invalid': errorMessage }" v-bind="$attrs">
        <select id="choices-gender" ref="genderSelect" v-model="localValue" class="form-control" name="choices-gender">
            <option value="" selected>Seleccionar</option>
            <option v-for="gender in genderOptions" :key="gender" :value="gender">
                {{ getGender(gender) }}
            </option>
        </select>
    </div>
    <div v-if="errorMessage" class="invalid-feedback">{{ errorMessage }}</div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick, watch, computed } from 'vue';
import Choices from "choices.js";
import { getGenderName } from '@/utils/globalFunctions.js';

const props = defineProps({
    modelValue: String,
    errors: Object,
    errorFieldName: String,
});
const emit = defineEmits(['update:modelValue']);

const genderOptions = ["male", "female", "other"];
const genderChoices = ref(null);
const genderSelect = ref(null);

const getGender = (genderCode) => {
    return getGenderName(genderCode);
};

const initializeChoices = async () => {
    await nextTick(); // Asegura que el select está en el DOM antes de inicializar Choices.js
    if (genderSelect.value) {
        destroyChoices(); // Asegura que no hay una instancia anterior de Choices.js
        genderChoices.value = new Choices(genderSelect.value, {
            searchEnabled: false,
            allowHTML: true,
            placeholder: true,
            itemSelectText: ""
        });

        // Sincronizar el valor de Choices.js con el `modelValue`
        genderSelect.value.addEventListener('change', (event) => {
            emit('update:modelValue', event.target.value);
        });

        // Establecer el valor inicial en Choices.js basado en modelValue
        updateSelectedValue();

        // Escuchar cuando se abre el dropdown para resaltar la opción seleccionada
        genderSelect.value.addEventListener("showDropdown", highlightSelectedOption);
    }
};

// Función para resaltar la opción seleccionada cuando el menú se abre
const highlightSelectedOption = () => {
    if (!genderChoices.value || !props.modelValue) return;

    const selectedVal = props.modelValue;
    const el = document.querySelector(`.choices__list--dropdown .choices__item[data-value="${selectedVal}"]`);

    if (el) {
        genderChoices.value._highlightChoice(el);
    }
};

// Función para actualizar el valor seleccionado en Choices.js
const updateSelectedValue = () => {
    if (genderChoices.value) {
        genderChoices.value.setChoiceByValue(props.modelValue || '');
    }
};

const destroyChoices = () => {
    if (genderChoices.value) {
        genderChoices.value.destroy();
        genderChoices.value = null;
    }
};

const localValue = computed({
    get() {
        return props.modelValue;
    },
    set(value) {
        emit('update:modelValue', value);
        if (genderChoices.value) {
            genderChoices.value.setChoiceByValue(value);
        }
    }
});

const errorMessage = computed(() => {
    return props.errors?.[props.errorFieldName]?.[0] || null;
});

onMounted(() => {
    initializeChoices();
});

onBeforeUnmount(() => {
    destroyChoices();
});

// Vuelve a inicializar Choices.js si `modelValue` cambia
watch(() => props.modelValue, () => {
    updateSelectedValue();
});
</script>

<style>
.is-invalid {
    border: 0px solid red;
    border-radius: 10px;
}

.is-invalid .choices__inner {
    border: 1px solid red !important;
}

/* Quitar flecha nativa y apariencia del <select> original */
.gender-select-wrapper select.form-control {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background-image: none !important;
}

/* Ancho y layout del contenedor de Choices */
.gender-select-wrapper {
    overflow: visible !important;
}

.gender-select-wrapper .choices {
    width: 100%;
    margin-bottom: 0;
    overflow: visible !important;
}

/* Input visual - diseño moderno y sutil */
.gender-select-wrapper .choices__inner {
    min-height: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    border-radius: 12px;
    border: 1.5px solid #e2e8f0;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    padding: 6px 40px 6px 14px;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    font-size: 0.9375rem;
}

.gender-select-wrapper .choices__inner:hover {
    border-color: #cbd5e1;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

/* Flecha sutil y moderna */
.gender-select-wrapper .choices[data-type*="select-one"]::after {
    border: none;
    content: '';
    position: absolute;
    right: 16px;
    top: 50%;
    width: 0;
    height: 0;
    margin-top: -2px;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 5px solid #94a3b8;
    pointer-events: none;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.gender-select-wrapper .choices[data-type*="select-one"].is-open::after {
    border-top: none;
    border-bottom: 5px solid #667eea;
    margin-top: -3px;
    transform: rotate(0deg);
}

/* Dropdown moderno y sutil - oculto por defecto */
.gender-select-wrapper .choices__list--dropdown {
    display: none !important;
}

.gender-select-wrapper .is-open .choices__list--dropdown,
.gender-select-wrapper .choices__list--dropdown.is-active {
    display: block !important;
    position: absolute !important;
    top: 100% !important;
    left: 0 !important;
    right: 0 !important;
    margin: -1.5px 0 0 0 !important;
    padding: 4px 0 !important;
    border: 1.5px solid #e2e8f0 !important;
    border-top: 1.5px solid transparent !important;
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;
    border-bottom-left-radius: 12px !important;
    border-bottom-right-radius: 12px !important;
    background: #ffffff !important;
    z-index: 10060 !important;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08), 0 4px 10px rgba(0, 0, 0, 0.04) !important;
    transform: none !important;
    max-height: 280px !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    visibility: visible !important;
    pointer-events: auto !important;
    opacity: 1 !important;
}

/* Elimina cualquier separación o elemento decorativo */
.gender-select-wrapper .choices__list--dropdown::before,
.gender-select-wrapper .choices__list--dropdown::after {
    display: none !important;
}

/* Estado abierto - integración perfecta sin separación */
.gender-select-wrapper .is-open .choices__inner {
    border-bottom-left-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
    border-bottom: 1.5px solid transparent !important;
    border-color: #e2e8f0;
    box-shadow: 0 0 0 1px rgba(102, 126, 234, 0.1);
    background: #fafbfc;
}

/* Evita flip y cualquier cambio de posición */
.gender-select-wrapper .is-flipped .choices__list--dropdown {
    bottom: auto !important;
    top: 100% !important;
    margin: -1.5px 0 0 0 !important;
    transform: none !important;
}

/* Oculta el placeholder vacío */
.gender-select-wrapper .choices__list--dropdown .choices__item[data-value=""],
.gender-select-wrapper .choices__list--dropdown .choices__placeholder {
    display: none !important;
}

/* Scrollbar minimalista y sutil */
.gender-select-wrapper .choices__list--dropdown::-webkit-scrollbar {
    width: 5px;
}

.gender-select-wrapper .choices__list--dropdown::-webkit-scrollbar-track {
    background: transparent;
}

.gender-select-wrapper .choices__list--dropdown::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.2);
    border-radius: 10px;
}

.gender-select-wrapper .choices__list--dropdown::-webkit-scrollbar-thumb:hover {
    background: rgba(148, 163, 184, 0.4);
}

/* Items - diseño sutil y minimalista */
.gender-select-wrapper .choices__list--dropdown .choices__item {
    padding: 10px 16px;
    line-height: 1.5;
    margin: 0;
    font-size: 0.9375rem;
    color: #475569;
    transition: all 0.2s ease;
    cursor: pointer;
    border-left: 2px solid transparent;
    background: transparent;
}

/* Hover muy sutil */
.gender-select-wrapper .choices__list--dropdown .choices__item:hover,
.gender-select-wrapper .choices__list--dropdown .choices__item.is-highlighted {
    background: #f8fafc;
    color: #1e293b;
}

/* Item seleccionado con indicador minimalista */
.gender-select-wrapper .choices__list--dropdown .choices__item.is-selected {
    background: #f1f5f9;
    color: #475569;
    font-weight: 500;
    position: relative;
}

/* Checkmark sutil para item seleccionado */
.gender-select-wrapper .choices__list--dropdown .choices__item.is-selected::after {
    content: '✓';
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #64748b;
    font-weight: 600;
    font-size: 0.875rem;
}

/* Espaciado para primera y última opción */
.gender-select-wrapper .choices__list--dropdown .choices__item:first-child {
    margin-top: 2px;
}

.gender-select-wrapper .choices__list--dropdown .choices__item:last-child {
    margin-bottom: 2px;
}

/* Animación suave y sutil al abrir */
.gender-select-wrapper .choices__list--dropdown.is-active {
    animation: fadeInDropdown 0.2s ease-out;
}

@keyframes fadeInDropdown {
    from {
        opacity: 0;
        transform: translateY(-4px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.gender-select-wrapper .choices__list--single .choices__item {
    font-size: 0.9375rem;
    color: #1e293b;
    font-weight: 500;
}

/* Texto del placeholder más sutil */
.gender-select-wrapper .choices__list--single .choices__placeholder {
    opacity: 0.5;
    color: #94a3b8;
}
</style>
